import React, { useState } from "react";
import { observer } from "mobx-react";
import Play from "../Icons/Play.svg";
import Pause from "../Icons/Pause.svg";
import Logo from "../../../logo-no-text.png";
import { Menu, MenuItem, IconButton } from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  RssFeed as RssFeedIcon,
} from "@mui/icons-material";
import LazyLoad from "react-lazyload";
import ShareModal from '../../../Components/ShareModal/index';
import { deleteUserFeed, addUserFeed, createReactivationCheckoutSession } from "../../../audiblogsApi";
import { NotificationManager } from "react-notifications";
import store from "../store";
import { getUserFeeds } from "../../../audiblogsApi";
import userStore from "../../../Stores/User";
import { isPaidProUser } from "../../../constants/plans";
import { Modal, mergeStyleSets } from '@fluentui/react';
import gamificationStore from '../../../Stores/GamificationStore';


const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: 'rgb(66 59 74)',
    width: '300px',
    padding: '10px',
    fontFamily: 'Cabin',
    fontSize: '18px',
    textAlign: 'center',
    minHeight: '108px',
    borderRadius: '14px',
  },
});

const getHostName = (link) => {
  if (link) {
    const doc = document.createElement("a");
    doc.href = (link.startsWith("http") ? "" : "http://") + link;
    return doc.hostname;
  }
  return "";
};

const estimatedMinutes = (numCharacters) =>
  Math.round(((numCharacters / 900))) || 5; // Default to 5 minutes if no characters count

const getMp3Prefix = (article) => {
  try {
    if (!article || !article.audio_url) {
      return `/404`;
    }
    return `/share/${article.audio_url.split("/")[4].replace(".mp3", "")}`;
  } catch (e) {
    return `/404`;
  }
};

const FeedSubscribed = observer(
  ({ feed, idToken, selectArticle, isActive, onUnsubscribe, isSubscribed = true }) => {
    const {
      feed_id,
      title,
      date_added,
      num_characters,
      thumbnail_url,
      last_article_url,
      description,
      audio_url,
      article_id,
      latestArticle
    } = feed;

    const feedDate = new Date(date_added || Date.now());
    const [isHovering, setIsHovering] = useState(false);
    const [menuAnchorElement, setAnchorEl] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isUnsubscribing, setIsUnsubscribing] = useState(false);
    const [unsubscribed, setUnsubscribed] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Check if this feed's article is currently playing
    const isCurrentlyPlaying = (
      store.isPlaying && 
      store.currentArticle && 
      (store.currentArticle.article_id === article_id || 
       store.currentArticle.article_id === feed_id ||
       (latestArticle && store.currentArticle.article_id === latestArticle.article_id))
    );

    const onImageLoaded = () => setImageLoaded(true);

    const handleMenuClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const shareFeed = () => {
      handleMenuClose();
      // Navigate to feed landing page without encoding slashes
      const encodedFeedId = encodeURIComponent(feed_id).replace(/%2F/g, '/');
      window.open(`/feed/${encodedFeedId}`, '_blank');
    };

    const handlePlayPause = (e) => {
      e.stopPropagation();
      
      // Create a playable article object
      const playableArticle = latestArticle || {
        ...feed,
        article_id: article_id || feed_id, // Use article_id if available, otherwise feed_id
        title: feed.article_title || title,
        url: feed.article_url || last_article_url,
        feed_title: title,
        feed_id: feed_id,
        thumbnail_url: feed.thumbnail_url // Add thumbnail_url from feed
      };
      
      // If it's already playing, toggle pause, otherwise force play
      if (isCurrentlyPlaying) {
        selectArticle(playableArticle); // This will toggle pause
      } else {
        // Force play by using the store's changeCurrentArticleForcePlay method
        store.changeCurrentArticleForcePlay(playableArticle);
      }
    };

    const handleSubscribe = async () => {
      if (isSubscribing) return;
      
      try {
        setIsSubscribing(true);
        
        // Get current feeds to check limit
        const response = await getUserFeeds(idToken);
        const userFeeds = response.data.feeds || [];
        
        // Check if user is on any paid plan
        const isPaidUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
          
        // If not on any paid plan and already at 3 feeds, show upgrade modal
        if (!isPaidUser && userFeeds.length >= 3) {
          setModalContent({
            title: "Upgrade Required",
            message: "You're already subscribed to 3 feeds. Please upgrade to a paid plan to subscribe to unlimited feeds.",
            actionText: "Upgrade Now",
            actionHandler: () => {
              // Check if user has a customer_id but no active subscription (cancelled)
              const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
              const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
              
              if (hasStripeCustomerId && !hasActiveSubscription) {
                createReactivationCheckoutSession(userStore.idToken, {}, '/add')
                  .then(response => {
                    if (response.data.url) {
                      window.location.href = response.data.url;
                    } else {
                      window.location.href = '/onboarding/choosevoice';
                    }
                  })
                  .catch(() => {
                    window.location.href = '/onboarding/choosevoice';
                  });
              } else {
                window.location.href = '/onboarding/choosevoice';
              }
              hideModal();
            }
          });
          showModal();
          return;
        }

        await addUserFeed(idToken, feed_id);
        NotificationManager.success("Successfully subscribed to feed", "", 2000);
        
        // Award bonus characters for subscribing to feed
        gamificationStore.completeAction(gamificationStore.actions.SUBSCRIBE_FEED);
        
        if (onUnsubscribe) {
          onUnsubscribe(feed_id);
        }
      } catch (error) {
        console.error("Failed to subscribe:", error);
        NotificationManager.info("Failed to subscribe to feed. Please signup or go to the Feeds page to subscribe.", "", 5000);
      } finally {
        setIsSubscribing(false);
      }
    };

    const handleUnsubscribe = async () => {
      if (isUnsubscribing) return;
      console.log("unsubscribing", feed_id, idToken);
      
      try {
        setIsUnsubscribing(true);
        await deleteUserFeed(idToken, feed_id);
        setUnsubscribed(true);
        NotificationManager.success("Successfully unsubscribed from feed", "", 2000);
        
        if (onUnsubscribe) {
          onUnsubscribe(feed_id);
        }
      } catch (error) {
        console.error("Failed to unsubscribe:", error);
        NotificationManager.error("Failed to unsubscribe from feed", "", 2000);
        // Don't set unsubscribed to true if the API call failed
      } finally {
        setIsUnsubscribing(false);
      }
    };

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Use the feed's hostname as display text
    const displayHostname = getHostName(feed_id || last_article_url || "");

    // Create a modified feed object for ShareModal to prevent audio_url.split error
    const shareableFeed = {
      ...feed,
      audio_url: audio_url || "https://audioread.com/share/placeholder.mp3"
    };

    const showModal = () => {
      setIsModalOpen(true);
    };

    const hideModal = () => {
      setIsModalOpen(false);
    };

    return (
      <>
        <Modal
          isOpen={isModalOpen}
          onDismiss={hideModal}
          isBlocking={false}
          isDarkOverlay={true}
          containerClassName={contentStyles.container}
        >
          <div>
            {modalContent && (
              <>
                <div style={{ color: 'white', marginBottom: '15px', fontWeight: 'bold' }}>
                  {modalContent.title}
                </div>
                <div style={{ color: 'white', marginBottom: '15px' }}>
                  {modalContent.message}
                </div>
                {modalContent.actionText && (
                  <button
                    onClick={modalContent.actionHandler}
                    className="clickable"
                    style={{
                      backgroundColor: '#1976d2',
                      border: 'none',
                      color: 'white',
                      borderRadius: '9px',
                      marginTop: '14px',
                      marginRight: '10px',
                      fontSize: '16px',
                      padding: '8px 16px',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {modalContent.actionText}
                  </button>
                )}
                <button
                  onClick={hideModal}
                  className="clickable"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid lightgrey',
                    color: 'white',
                    borderRadius: '9px',
                    marginTop: '14px',
                    fontSize: '16px',
                    padding: '2px 8px',
                    outline: 'none',
                  }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </Modal>
        <div className="article-card feed-card">
          <div
            className="article-background-img"
            style={{
              backgroundImage: `url(${imageLoaded && thumbnail_url ? thumbnail_url : Logo})`,
            }}
          />
          <div className="article">
            <div className="context-menu" style={{ marginRight: '10px' }}>
              <IconButton onClick={shareFeed}>
                <RssFeedIcon htmlColor="#ffffff" sx={{ fontSize: 20 }} />
              </IconButton>
            </div>
            <div
              className="thumbnail"
              onClick={handlePlayPause}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {thumbnail_url ? (
                <LazyLoad height={145} offset={150}>
                  <img src={thumbnail_url} onLoad={onImageLoaded} alt={title} />
                </LazyLoad>
              ) : (
                <div className="feed-placeholder">
                  <img src={Logo} alt="Feed" />
                </div>
              )}
            </div>
            <div
              className="title"
              onClick={handlePlayPause}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {title || "Untitled Feed"}
            </div>
            <div 
              className="author" 
              style={{
                marginTop: '-5px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxHeight: '4.5em',
                lineHeight: '1.5',
                width: '100%',
                minWidth: 0,
                flex: '1',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!description) return; // Don't show tooltip if no description
                
                const tooltip = document.createElement('div');
                tooltip.className = 'description-tooltip';
                tooltip.style.position = 'fixed';
                tooltip.style.backgroundColor = 'rgba(0,0,0,0.9)';
                tooltip.style.color = 'white';
                tooltip.style.padding = '10px';
                tooltip.style.borderRadius = '4px';
                tooltip.style.zIndex = '9999';
                tooltip.style.maxWidth = '300px';
                tooltip.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                tooltip.style.fontSize = '14px';
                tooltip.style.whiteSpace = 'normal';
                tooltip.style.overflow = 'auto';
                tooltip.style.maxHeight = '200px';
                tooltip.innerText = description;
                
                const rect = e.currentTarget.getBoundingClientRect();
                tooltip.style.top = `${rect.bottom + 5}px`;
                tooltip.style.left = `${rect.left}px`;
                
                document.body.appendChild(tooltip);
              }}
              onMouseLeave={(e) => {
                const tooltip = document.querySelector('.description-tooltip');
                if (tooltip) {
                  tooltip.remove();
                }
              }}
            >
              {description || feed_id}
            </div>
            <div className="time" onClick={handlePlayPause}>
              <img 
                src={isCurrentlyPlaying ? Pause : Play} 
                alt={isCurrentlyPlaying ? "Pause" : "Play"} 
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
            </div>
            <div
              className="add-to-playlist unsubscribe-button"
              onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
              style={{ 
                cursor: isSubscribed ? (isUnsubscribing ? 'default' : 'pointer') : (isSubscribing ? 'default' : 'pointer'),
                opacity: isSubscribed ? (isUnsubscribing ? 0.7 : 1) : (isSubscribing ? 0.7 : 1),
                backgroundColor: isSubscribed ? '#ffd700' : '#1976d2',
                color: isSubscribed ? '#000' : 'white'
              }}
            >
              <span>
                {isSubscribed 
                  ? (isUnsubscribing ? "Unsubscribing..." : "Unsubscribe")
                  : (isSubscribing ? "Subscribing..." : "Subscribe")}
              </span>
            </div>
          </div>
        </div>
        <ShareModal 
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          article={shareableFeed}
        />
      </>
    );
  }
);

export default FeedSubscribed; 